import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { getSelfNotes, createSelfNote, deleteSelfNote, updateSelfNote, type SelfNote } from '@/services/notes'
import { uploadMedia } from '@/services/media'
import { queryKeys } from '@/lib/query/keys'
import { useNotesStore, type NoteColor } from '@/stores/notes-store'
// Import directly from auth-store instead of barrel export to avoid loading all stores
import { useAuthStore } from '@/stores/auth-store'
import { CreateNoteForm } from '@/components/notes/create-note-form'
import { NotesGrid } from '@/components/notes/notes-grid'
import { AttachmentViewer } from '@/components/notes/attachment-viewer'
import { ModuleLimitDialog } from '@/components/shared/module-limit-dialog'
import { useModuleQuota } from '@/hooks/use-module-quota'

// Constants
const AVAILABLE_COLORS: NoteColor[] = ['bg-yellow-300', 'bg-green-300', 'bg-blue-300', 'bg-red-300']
const DEFAULT_COLOR: NoteColor = 'bg-yellow-300'
const ARCHIVE_COLOR: NoteColor = 'bg-gray-300'

// Types
interface Attachment {
  id: string
  name: string
  url: string
  type: 'image' | 'audio' | 'file'
}

// Utility functions
function getRandomRotation(index: number): number {
  const rotations = [2, -2, 1, -1, 3, -3]
  return rotations[index % rotations.length]
}

function formatReminder(reminder: string | null): string | null {
  if (!reminder) return null

  try {
    const timestamp = typeof reminder === 'string' && /^\d+$/.test(reminder)
      ? parseInt(reminder, 10)
      : new Date(reminder).getTime()

    if (isNaN(timestamp) || timestamp <= 0) {
      return null
    }

    const date = new Date(timestamp)

    if (isNaN(date.getTime())) {
      return null
    }

    const dateStr = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })

    const timeStr = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })

    return `${dateStr} ${timeStr}`
  } catch (error) {
    console.error('Error formatting reminder:', error)
    return null
  }
}

function generateDeterministicId(url: string, type: string, index: number): string {
  // Create a simple hash from URL for determinism
  let hash = 0
  for (let i = 0; i < url.length; i++) {
    const char = url.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32-bit integer
  }
  // Combine hash with type and index for uniqueness
  return `fallback-${Math.abs(hash)}-${type}-${index}`
}

function parseAttachments(
  attachments: Record<string, unknown> | null | string
): Attachment[] {
  if (!attachments) {
    return []
  }

  let parsedAttachments: Record<string, unknown> = attachments as Record<string, unknown>
  if (typeof attachments === 'string') {
    try {
      parsedAttachments = JSON.parse(attachments)
    } catch (e) {
      console.error('Failed to parse attachments as JSON:', e)
      return []
    }
  }

  if (!parsedAttachments || typeof parsedAttachments !== 'object' || Array.isArray(parsedAttachments)) {
    return []
  }

  const parsed: Attachment[] = []

  // Handle update API format: { deletedIds: [], images: [...] }
  if (parsedAttachments.images && Array.isArray(parsedAttachments.images)) {
    (parsedAttachments.images as Record<string, unknown>[]).forEach((img) => {
      if (img && typeof img === 'object' && !Array.isArray(img)) {
        const url = (img.url || img.image) as string | undefined
        if (url && typeof url === 'string') {
          parsed.push({
            id: String(img.id || generateDeterministicId(url, 'image', parsed.length)),
            name: url.split('/').pop() || url.split('\\').pop() || 'Image',
            url: url,
            type: 'image' as const,
          })
        }
      }
    })
  }

  // Handle create API format: { Image: [...], Audio: [...], File: [...] }
  if (parsedAttachments.Image) {
    const imageArray = Array.isArray(parsedAttachments.Image)
      ? parsedAttachments.Image
      : [parsedAttachments.Image]

    ;(imageArray as Record<string, unknown>[]).forEach((img) => {
      if (img && typeof img === 'object' && !Array.isArray(img)) {
        const url = (img.image || img.url) as string | undefined
        if (url && typeof url === 'string') {
          parsed.push({
            id: String(img.id || generateDeterministicId(url, 'image', parsed.length)),
            name: url.split('/').pop() || url.split('\\').pop() || 'Image',
            url: url,
            type: 'image' as const,
          })
        }
      }
    })
  }

  if (parsedAttachments.Audio) {
    const audioArray = Array.isArray(parsedAttachments.Audio)
      ? parsedAttachments.Audio
      : [parsedAttachments.Audio]

    ;(audioArray as Record<string, unknown>[]).forEach((audio) => {
      if (audio && typeof audio === 'object') {
        const url = (audio.image || audio.url) as string | undefined
        if (url && typeof url === 'string') {
          parsed.push({
            id: String(audio.id || generateDeterministicId(url, 'audio', parsed.length)),
            name: url.split('/').pop() || url.split('\\').pop() || 'Audio',
            url: url,
            type: 'audio',
          })
        }
      }
    })
  }

  if (parsedAttachments.File) {
    const fileArray = Array.isArray(parsedAttachments.File)
      ? parsedAttachments.File
      : [parsedAttachments.File]

    ;(fileArray as Record<string, unknown>[]).forEach((file) => {
      if (file && typeof file === 'object') {
        const url = (file.image || file.url) as string | undefined
        if (url && typeof url === 'string') {
          parsed.push({
            id: String(file.id || generateDeterministicId(url, 'file', parsed.length)),
            name: url.split('/').pop() || url.split('\\').pop() || 'File',
            url: url,
            type: 'file',
          })
        }
      }
    })
  }

  return parsed
}

export const Route = createFileRoute('/(app)/productivity/notes/')({
  component: NotesPage,
})

function NotesPage() {
  const queryClient = useQueryClient()
  const { setNoteColor, getNoteColor } = useNotesStore()

  // How many notes the plan still allows today.
  const noteQuota = useModuleQuota('notes')
  const [noteLimitReached, setNoteLimitReached] = useState(false)

  const [newNote, setNewNote] = useState({
    title: '',
    content: '',
    color: DEFAULT_COLOR,
    reminder: '',
  })
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([])
  const [uploadedAttachments, setUploadedAttachments] = useState<Attachment[]>([])
  const [selectedAttachment, setSelectedAttachment] = useState<Attachment | null>(null)
  const [editingNote, setEditingNote] = useState<SelfNote | null>(null)
  const [existingAttachments, setExistingAttachments] = useState<Attachment[]>([])
  const [deletedAttachmentIds, setDeletedAttachmentIds] = useState<string[]>([])

  const formRef = useRef<HTMLDivElement>(null)

  const user = useAuthStore((state) => state.user)

  // Scroll to form when editing a note
  useEffect(() => {
    if (editingNote && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [editingNote])

  // Fetch notes
  const { data: notesResponse, isLoading } = useQuery({
    queryKey: queryKeys.notes.list({
      page_limit: 100,
      pagination: 'Y',
      page_no: 1,
      search: '',
      start_date: '',
      end_date: '',
      sort_field: 'id',
      sort_direction: 'DESC',
      self_note_id: '',
    }),
    queryFn: () =>
      getSelfNotes({
        page_limit: 100,
        pagination: 'Y',
        page_no: 1,
        search: '',
        start_date: '',
        end_date: '',
        sort_field: 'id',
        sort_direction: 'DESC',
        self_note_id: '',
      }),
  })

  const notes = notesResponse?.data || []

  // Upload media mutation
  const uploadMediaMutation = useMutation({
    mutationFn: uploadMedia,
  })

  // Create note mutation
  const createNoteMutation = useMutation({
    mutationFn: createSelfNote,
    onSuccess: (response) => {
      toast.success('Note created successfully')
      setNewNote({ title: '', content: '', color: DEFAULT_COLOR, reminder: '' })
      setAttachmentFiles([])
      setUploadedAttachments([])
      // Set color for the newly created note
      if (response.data) {
        setNoteColor(response.data.id, newNote.color)
      }
      // Invalidate all notes queries (list and reminders) to ensure calendar updates
      queryClient.invalidateQueries({ queryKey: ['notes'] })
      // Today's note allowance just went down by one. The server owns that
      // count, so re-read it instead of decrementing a local copy.
      queryClient.invalidateQueries({
        queryKey: queryKeys.subscription.transaction(),
      })
    },
    onError: (error: Error) => {
      toast.error(error?.message || 'Failed to create note')
    },
  })

  // Delete note mutation
  const deleteNoteMutation = useMutation({
    mutationFn: deleteSelfNote,
    onSuccess: () => {
      toast.success('Note deleted successfully')
      // Don't clear note color here - preserve it during deletion
      // The note will be removed from the list after refetch, so the color
      // doesn't need to be cleared immediately
      // Invalidate all notes queries (list and reminders) to ensure calendar updates
      queryClient.invalidateQueries({ queryKey: ['notes'] })
    },
    onError: (error: Error) => {
      toast.error(error?.message || 'Failed to delete note')
    },
  })

  // Update note mutation
  const updateNoteMutation = useMutation({
    mutationFn: updateSelfNote,
    onSuccess: (response, variables) => {
      toast.success('Note updated successfully')
      
      // Only reset state if this update matches the currently editing note
      // This prevents race conditions when user edits a different note while update is in-flight
      if (editingNote && editingNote.id === Number(variables.id)) {
        setEditingNote(null)
        setExistingAttachments([])
        setDeletedAttachmentIds([])
        setAttachmentFiles([])
        setUploadedAttachments([])
        setNewNote({ title: '', content: '', color: DEFAULT_COLOR, reminder: '' })
        
        // Set color for the updated note if it changed
        if (response.data) {
          const currentColor = getNoteColor(editingNote.id)
          if (currentColor !== newNote.color) {
            setNoteColor(response.data.id, newNote.color)
          }
        }
      }
      
      // Always invalidate queries to refresh the notes list
      queryClient.invalidateQueries({ queryKey: ['notes'] })
    },
    onError: (error: Error) => {
      toast.error(error?.message || 'Failed to update note')
    },
  })

  // Handle attachment file changes
  const handleAttachmentFilesChange = (files: File[]) => {
    setAttachmentFiles(files)
  }

  // Handle file upload
  const handleAttachmentUpload = async (
    files: File[],
    {
      onProgress,
      onSuccess,
      onError,
    }: {
      onProgress: (file: File, progress: number) => void
      onSuccess: (file: File) => void
      onError: (file: File, error: Error) => void
    }
  ) => {
    if (files.length === 0 || !user) {
      return
    }

    const deviceId =
      user.user_device_id && user.user_device_id.trim().length > 0
        ? user.user_device_id
        : 'WEB_CLIENT'
    const timezone = user.user_time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone

    for (const file of files) {
      try {
        onProgress(file, 0)
        const response = await uploadMediaMutation.mutateAsync({
          device_id: deviceId,
          user_id: user.id,
          timezone,
          files: [file],
        })

        const result = response?.result?.[0]
        if (!result) {
          throw new Error('Invalid upload response')
        }

        // Determine attachment type
        let attachmentType: 'image' | 'audio' | 'file' = 'file'
        if (file.type.startsWith('image/')) {
          attachmentType = 'image'
        } else if (file.type.startsWith('audio/')) {
          attachmentType = 'audio'
        }

        // Add to uploaded attachments
        const attachment: Attachment = {
          id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
          name: file.name,
          url: result.original_file,
          type: attachmentType,
        }

        setUploadedAttachments((prev) => [...prev, attachment])
        onProgress(file, 100)
        onSuccess(file)
        toast.success('File uploaded successfully')
      } catch (error) {
        const err = error instanceof Error ? error : new Error('Failed to upload media')
        onError(file, err)
        toast.error(err.message)
        setAttachmentFiles((prev) => prev.filter((item) => item !== file))
      }
    }
  }

  const onFileReject = (file: File, message: string) => {
    toast.error(message, {
      description: `"${file.name.length > 20 ? `${file.name.slice(0, 20)}...` : file.name}" was rejected`,
    })
  }

  // Format attachments for API
  const formatAttachments = (forUpdate = false) => {
    if (forUpdate) {
      // For update: use same format as create mode for consistency
      // Group attachments by type: Image, Audio, File
      const grouped: Record<string, Array<{ image: string; id: number }>> = {}

      // Add existing attachments that weren't deleted
      existingAttachments.forEach((attachment) => {
        if (!deletedAttachmentIds.includes(attachment.id)) {
          const numericId = Number(attachment.id)
          // Process all attachments, but use real server ID when available
          let apiType = 'File'
          if (attachment.type === 'image') {
            apiType = 'Image'
          } else if (attachment.type === 'audio') {
            apiType = 'Audio'
          }

          if (!grouped[apiType]) {
            grouped[apiType] = []
          }

          grouped[apiType].push({
            image: attachment.url,
            // Use real numeric server ID when available, otherwise treat as new (id 0)
            id: Number.isFinite(numericId) && numericId > 0 ? numericId : 0,
          })
        }
      })

      // Add newly uploaded attachments
      uploadedAttachments.forEach((attachment) => {
        let apiType = 'File'
        if (attachment.type === 'image') {
          apiType = 'Image'
        } else if (attachment.type === 'audio') {
          apiType = 'Audio'
        }

        if (!grouped[apiType]) {
          grouped[apiType] = []
        }

        grouped[apiType].push({
          image: attachment.url,
          id: 0, // New attachments have id 0
        })
      })

      // Include deletedIds for update API compatibility
      const deletedIds = deletedAttachmentIds
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0)

      // Return format matching create mode, with deletedIds for update
      return {
        ...grouped,
        ...(deletedIds.length > 0 && { deletedIds }),
      }
    } else {
      // For create: use existing format
      if (uploadedAttachments.length === 0) {
        return {}
      }

      const grouped: Record<string, Array<{ image: string; id: number }>> = {}

      uploadedAttachments.forEach((attachment) => {
        let apiType = 'File'
        if (attachment.type === 'image') {
          apiType = 'Image'
        } else if (attachment.type === 'audio') {
          apiType = 'Audio'
        }

        if (!grouped[apiType]) {
          grouped[apiType] = []
        }

        grouped[apiType].push({
          image: attachment.url,
          id: 0, // New attachments should not send client-generated IDs
        })
      })

      return grouped
    }
  }

  // Edit handler functions
  const handleEditNote = (note: SelfNote) => {
    setEditingNote(note)

    // Parse existing attachments
    const parsed = parseAttachments(note.attachments)
    setExistingAttachments(parsed)
    setDeletedAttachmentIds([])

    // Convert reminder timestamp to datetime-local format (in user's local timezone)
    const reminderDate = note.reminder
      ? (() => {
          try {
            // Use the same parsing logic as formatReminder for consistency
            const timestamp = typeof note.reminder === 'string' && /^\d+$/.test(note.reminder)
              ? parseInt(note.reminder, 10)
              : new Date(note.reminder).getTime()

            if (isNaN(timestamp) || timestamp <= 0) {
              return ''
            }

            const date = new Date(timestamp)

            if (isNaN(date.getTime())) {
              return ''
            }

            // Format in local timezone for datetime-local input (YYYY-MM-DDTHH:mm)
            const year = date.getFullYear()
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const day = String(date.getDate()).padStart(2, '0')
            const hours = String(date.getHours()).padStart(2, '0')
            const minutes = String(date.getMinutes()).padStart(2, '0')
            return `${year}-${month}-${day}T${hours}:${minutes}`
          } catch (error) {
            console.error('Error parsing reminder for edit mode:', error)
            return ''
          }
        })()
      : ''

    // Get current note color
    const currentColor = getNoteColor(note.id)

    setNewNote({
      title: note.title,
      content: note.message,
      color: currentColor,
      reminder: reminderDate,
    })

    setAttachmentFiles([])
    setUploadedAttachments([])
  }

  const handleCancelEdit = () => {
    setEditingNote(null)
    setExistingAttachments([])
    setDeletedAttachmentIds([])
    setAttachmentFiles([])
    setUploadedAttachments([])
    setNewNote({ title: '', content: '', color: DEFAULT_COLOR, reminder: '' })
  }

  const handleRemoveExistingAttachment = (id: string) => {
    setDeletedAttachmentIds((prev) => [...prev, id])
    setExistingAttachments((prev) => prev.filter((att) => att.id !== id))
  }

  const handleCreateNote = () => {
    if (!newNote.title.trim() || !newNote.content.trim()) {
      toast.error('Please fill in both title and content')
      return
    }

    // Capture the color before mutation
    const noteColor = newNote.color

    // Convert reminder datetime-local value to timestamp string
    const reminderTimestamp = newNote.reminder
      ? new Date(newNote.reminder).getTime().toString()
      : ''

    if (editingNote) {
      // Update existing note
      const attachments = formatAttachments(true)

      updateNoteMutation.mutate({
        id: editingNote.id,
        title: newNote.title.trim(),
        message: newNote.content.trim(),
        reminder: reminderTimestamp,
        attachments,
      })
    } else {
      // Daily allowance. Notes are always personal, so there is no company to
      // scope this to. Checked after the title/content validation so a note
      // that could not be saved anyway does not trigger a subscription prompt.
      if (noteQuota.isRestrictedEverywhere) {
        setNoteLimitReached(true)
        return
      }

      // Create new note
      const attachments = formatAttachments(false)

      createNoteMutation.mutate(
        {
          title: newNote.title.trim(),
          message: newNote.content.trim(),
          reminder: reminderTimestamp,
          attachments,
        },
        {
          onSuccess: (response) => {
            // Set color for the newly created note
            if (response.data) {
              setNoteColor(response.data.id, noteColor)
            }
            // Reset attachments
            setAttachmentFiles([])
            setUploadedAttachments([])
          },
        }
      )
    }
  }

  const handleDeleteNote = (id: number | string) => {
    // If deleting the note that's currently being edited, close the edit form
    if (editingNote && editingNote.id === Number(id)) {
      handleCancelEdit()
    }
    
    deleteNoteMutation.mutate({ id })
    // Removed: clearNoteColor(Number(id)) - now handled in mutation onSuccess
  }

  const handleArchiveNote = (id: number) => {
    setNoteColor(id, ARCHIVE_COLOR)
    toast.success('Note archived')
  }

  const handleChangeColor = (id: number, color: typeof AVAILABLE_COLORS[number] | typeof ARCHIVE_COLOR) => {
    setNoteColor(id, color)
  }

  // Get note color from store or default
  const getNoteColorWithDefault = (noteId: number) => {
    return getNoteColor(noteId)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">Loading notes...</div>
      </div>
    )
  }

  return (
    <div className="h-[calc(100vh-8rem)]">
      {/* Main Card with dotted border and dotted background */}
      <Card
        className={cn(
          'h-full border-dotted border-2',
          '[background-size:20px_20px]',
          '[background-image:radial-gradient(#d4d4d4_1px,transparent_1px)]',
          'dark:[background-image:radial-gradient(#404040_1px,transparent_1px)]',
          'bg-card'
        )}
      >
        <CardContent className="p-6 h-full overflow-y-auto">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Create/Edit Note Form - Always on Left */}
            <div 
              ref={formRef}
              className="lg:w-80 lg:sticky lg:top-6 lg:self-start lg:h-[calc(100vh-12rem)] lg:overflow-y-auto" 
              data-note-form
            >
              {editingNote && (
                <div className="mb-4 p-3 rounded-md bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                      Editing note
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleCancelEdit}
                      className="h-6 text-blue-700 dark:text-blue-300"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
              <CreateNoteForm
                formData={newNote}
                onFormDataChange={(data) => setNewNote({ ...newNote, ...data })}
                attachmentFiles={attachmentFiles}
                onAttachmentFilesChange={handleAttachmentFilesChange}
                existingAttachments={existingAttachments}
                onRemoveExistingAttachment={handleRemoveExistingAttachment}
                onUpload={handleAttachmentUpload}
                onFileReject={onFileReject}
                onSubmit={handleCreateNote}
                isSubmitting={createNoteMutation.isPending || updateNoteMutation.isPending}
                availableColors={AVAILABLE_COLORS}
                isEditMode={!!editingNote}
              />
            </div>

            {/* Notes Grid - Right Side */}
            <div className="flex-1">
              <NotesGrid
                notes={notes}
                getNoteColor={getNoteColorWithDefault}
                availableColors={AVAILABLE_COLORS}
                archiveColor={ARCHIVE_COLOR}
                onEdit={handleEditNote}
                onArchive={handleArchiveNote}
                onDelete={handleDeleteNote}
                onChangeColor={handleChangeColor}
                onAttachmentClick={setSelectedAttachment}
                isDeleting={deleteNoteMutation.isPending}
                formatReminder={formatReminder}
                parseAttachments={parseAttachments}
                getRandomRotation={getRandomRotation}
              />
            </div>
          </div>

          {/* Attachment Viewer Dialog */}
          <AttachmentViewer
            attachment={selectedAttachment}
            onClose={() => setSelectedAttachment(null)}
          />

          {/* Dismissing this leaves whatever was typed in the note form. */}
          <ModuleLimitDialog
            module="notes"
            open={noteLimitReached}
            onOpenChange={(open) => {
              if (!open) setNoteLimitReached(false)
            }}
          />
        </CardContent>
      </Card>
    </div>
  )
}
